import { useEffect, useMemo, useRef, useState } from "react";
import aa from "../AdminPortal/adminApp.module.css";
import { HubSheet } from "../AdminPortal/HubPwa";
import DaySessionPanel from "./DaySessionPanel";
import SessionCalendarCard from "./SessionCalendarCard";
import { sessionColor } from "./calendarUtils";

function dayTitle(day) {
  return new Date(`${day}T12:00:00`).toLocaleDateString("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

export default function CalendarSessionSheet({
  open,
  day,
  sessions,
  groups,
  branches,
  doctors = [],
  sessionId,
  onSelectSession,
  onClose,
  onUpdated,
}) {
  const dayList = useMemo(
    () =>
      [...sessions]
        .filter((sess) => sess.start_datetime?.slice(0, 10) === day)
        .sort(
          (a, b) => new Date(a.start_datetime) - new Date(b.start_datetime),
        ),
    [sessions, day],
  );

  const preview =
    dayList.find((sess) => sess.id === sessionId) ||
    (sessionId ? { id: sessionId } : null);

  const sheetBackRef = useRef(() => false);
  const [panelAction, setPanelAction] = useState(null);

  useEffect(() => {
    if (!sessionId) setPanelAction(null);
  }, [sessionId]);

  const showDetail = Boolean(sessionId);

  const sheetHeadTitle =
    panelAction === "reschedule"
      ? "Перенос"
      : panelAction === "details"
        ? "Данные"
        : panelAction === "cancel"
          ? "Отмена"
          : "Занятие";

  const handleSheetBack = () => {
    if (sheetBackRef.current?.()) return;
    onSelectSession(null);
  };

  const sheetHeader = (
    <header className={aa.calSheetHead}>
      {showDetail ? (
        <button
          type="button"
          className={aa.calSheetBack}
          onClick={handleSheetBack}
        >
          ← Назад
        </button>
      ) : (
        <span className={aa.calSheetHeadSpacer} aria-hidden />
      )}
      <div className={aa.calSheetHeadCenter}>
        {showDetail ? (
          <p className={aa.calSheetHeadKicker}>{sheetHeadTitle}</p>
        ) : (
          <>
            <p className={aa.calSheetHeadKicker}>{dayTitle(day)}</p>
            <p className={aa.calSheetHeadSub}>
              {dayList.length}{" "}
              {dayList.length === 1 ? "занятие" : "занятий"}
            </p>
          </>
        )}
      </div>
      <button
        type="button"
        className={aa.calSheetClose}
        onClick={onClose}
        aria-label="Закрыть"
      >
        <svg viewBox="0 0 24 24" aria-hidden>
          <path
            d="M6 6l12 12M18 6 6 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </header>
  );

  return (
    <HubSheet
      open={open && Boolean(day)}
      onClose={onClose}
      titleId="cal-session-sheet-title"
      header={sheetHeader}
      sheetClassName={showDetail ? aa.calSheetHubDetail : aa.calSheetHub}
      bodyClassName={aa.calSheetBody}
    >
      {showDetail ? (
        <DaySessionPanel
          sessionId={sessionId}
          sessionPreview={
            preview?.start_datetime || preview?.title ? preview : null
          }
          groups={groups}
          branches={branches}
          doctors={doctors}
          onUpdated={onUpdated}
          variant="sheet"
          sheetBackRef={sheetBackRef}
          onPanelActionChange={setPanelAction}
        />
      ) : dayList.length === 0 ? (
        <div className={aa.calSheetEmpty}>
          <p className={aa.calSheetEmptyTitle}>Нет занятий</p>
          <p className={aa.calSheetEmptyHint}>
            В этот день ничего не запланировано
          </p>
        </div>
      ) : (
        <ul className={aa.calSheetList}>
          {dayList.map((sess) => {
            const groupIdx = groups.findIndex((g) => g.id === sess.group?.id);
            const color = sessionColor(sess, groupIdx);
            return (
              <li key={sess.id}>
                <SessionCalendarCard
                  sess={sess}
                  color={color}
                  variant="grid"
                  onClick={() => onSelectSession(sess.id)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </HubSheet>
  );
}
